import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Home, RotateCcw, Sparkles, Award, Trophy } from 'lucide-react';
import confetti from 'canvas-confetti';
import axios from 'axios';
import { RELIGIONS } from '../utils/godsQuestions';
import { awardBadge, getBadges } from '../utils/godsBadges';

const STAR_URL =
  'https://static.prod-images.emergentagent.com/jobs/0e7ec82b-be21-4167-ae09-299c58207254/images/51c2f4d391b08733a1282c5c803c88045bd7e3e5993310b66fbe2ae387825c7c.png';
const GODS_BG = 'https://static.prod-images.emergentagent.com/jobs/0e7ec82b-be21-4167-ae09-299c58207254/images/7b221cc89bb32c26551da609b6dc5932d3395b8dd600bcbd9048f845c7c2c510.jpeg';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GodsResultsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const savedRef = useRef(false);
  const [badgeState, setBadgeState] = useState({ earned: false, isNew: false, total: 0 });

  const score = parseInt(searchParams.get('score') || '0', 10);
  const total = parseInt(searchParams.get('total') || '20', 10);
  const religionId = searchParams.get('religion') || 'hinduism';
  const religion = RELIGIONS.find((r) => r.id === religionId) || RELIGIONS[0];
  const percentage = total > 0 ? Math.round((score / total) * 100) : 0;
  const stars = percentage >= 90 ? 3 : percentage >= 70 ? 2 : percentage >= 50 ? 1 : 0;

  useEffect(() => {
    if (!savedRef.current) {
      savedRef.current = true;
      axios
        .post(`${API}/gods/results`, {
          player_name: localStorage.getItem('gaurik_player_name') || 'Player',
          score,
          total_questions: total,
          religion: religionId,
        })
        .catch(console.error);

      if (score >= 18) {
        const alreadyHad = getBadges().includes(religionId);
        awardBadge(religionId);
        const collected = getBadges().length;
        setBadgeState({ earned: true, isNew: !alreadyHad, total: collected });
        confetti({ particleCount: 140, spread: 100, origin: { y: 0.6 }, colors: ['#fbbf24', '#f97316', '#fde68a', '#ffffff'] });
        if (collected === RELIGIONS.length) {
          const end = Date.now() + 3500;
          const gold = () => {
            confetti({ particleCount: 6, spread: 70, origin: { y: 0.4 }, colors: ['#fbbf24', '#f59e0b', '#fef3c7', '#fde047'], scalar: 1.2 });
            if (Date.now() < end) requestAnimationFrame(gold);
          };
          gold();
        }
      }
    }
    if (percentage >= 50) {
      const end = Date.now() + 2500;
      const frame = () => {
        confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors: ['#f97316', '#eab308', '#10b981'] });
        confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors: ['#f97316', '#eab308', '#10b981'] });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
    }
  }, [percentage, score, total, religionId]);

  const getMessage = () => {
    if (percentage >= 90) return 'AMAZING KNOWLEDGE!';
    if (percentage >= 70) return 'VERY WELL DONE!';
    if (percentage >= 50) return 'GOOD EFFORT!';
    return 'KEEP LEARNING!';
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-6"
      style={{ backgroundImage: `url(${GODS_BG})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}
    >
      <div className="bg-white/95 backdrop-blur-md border-4 border-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
        <Sparkles className="w-12 h-12 mx-auto mb-3 text-amber-500" />

        <h1 data-testid="gods-results-message" className="font-heading text-3xl sm:text-4xl font-black text-gray-900 mb-1">
          {getMessage()}
        </h1>
        <p data-testid="gods-results-religion" className="font-body text-sm text-gray-500 mb-5">
          {religion.name} round
        </p>

        <div className="flex justify-center gap-3 mb-5" data-testid="gods-results-stars">
          {[1, 2, 3].map((i) => (
            <img
              key={i}
              src={STAR_URL}
              alt={`star ${i}`}
              className={`w-14 h-14 animate-star-in ${i <= stars ? '' : 'grayscale opacity-30'}`}
              style={{ animationDelay: `${i * 250}ms` }}
            />
          ))}
        </div>

        <p data-testid="gods-results-score" className="font-heading text-5xl font-black text-gray-900 mb-1">
          {score}/{total}
        </p>
        <p className="font-body text-base font-bold text-gray-500 mb-6">{percentage}% correct</p>

        {badgeState.earned && (
          <div
            data-testid="gods-badge-earned"
            className="mb-6 rounded-3xl border-2 border-amber-200 bg-gradient-to-b from-amber-50 to-white p-5 animate-pop-in"
          >
            <div className="flex items-center justify-center gap-3 mb-2">
              <span
                className="w-14 h-14 rounded-full flex items-center justify-center text-white text-3xl leading-none shadow-lg ring-4 ring-amber-200"
                style={{ background: religion.gradient }}
                aria-hidden="true"
              >
                {religion.symbol}
              </span>
              <Award className="w-8 h-8 text-amber-500" />
            </div>
            <p className="font-heading text-lg font-black text-amber-700">
              {badgeState.isNew ? `${religion.name} Badge Unlocked!` : `${religion.name} Badge Kept!`}
            </p>
            <p className="font-body text-xs text-gray-500 mt-1">
              You have {badgeState.total} of {RELIGIONS.length} badges. Score 18 or more to collect them all!
            </p>
          </div>
        )}

        {badgeState.earned && badgeState.total === RELIGIONS.length && (
          <div
            data-testid="gods-master-trophy"
            className="mb-6 rounded-3xl p-6 animate-pop-in shadow-xl"
            style={{ background: 'linear-gradient(160deg, #78350f 0%, #b45309 45%, #f59e0b 100%)' }}
          >
            <div className="relative mx-auto mb-3 w-24 h-24 rounded-full flex items-center justify-center shadow-2xl ring-4 ring-amber-200/70"
              style={{ background: 'radial-gradient(circle at 30% 25%, #fffbeb, #fde047 35%, #f59e0b 75%, #b45309 100%)' }}
            >
              <Trophy className="w-12 h-12 text-amber-900" strokeWidth={2.5} />
            </div>
            <p className="font-heading text-2xl font-black text-amber-50 leading-tight">
              MASTER OF ALL FAITHS
            </p>
            <p className="font-body text-sm font-bold text-amber-100/90 mt-1">
              All four badges collected — the golden trophy is yours!
            </p>
            <div className="flex items-center justify-center gap-2 mt-3">
              {RELIGIONS.map((r) => (
                <span
                  key={`trophy-${r.id}`}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-base leading-none ring-2 ring-amber-200/70"
                  style={{ background: r.gradient }}
                  aria-hidden="true"
                >
                  {r.symbol}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <button
            data-testid="gods-play-again"
            onClick={() => navigate(`/gods/play?religion=${religion.id}`)}
            className="bg-gradient-to-r from-amber-500 to-orange-600 text-white font-black text-lg py-4 px-8 rounded-full border-b-[6px] border-orange-800 active:border-b-0 active:translate-y-[6px] transition-all hover:from-amber-400 hover:to-orange-500 flex items-center justify-center gap-2 shadow-lg"
          >
            <RotateCcw className="w-6 h-6" />
            Play Again
          </button>
          <button
            data-testid="gods-choose-religion"
            onClick={() => navigate('/gods')}
            className="bg-white text-gray-800 font-black text-base py-3 px-8 rounded-full border-2 border-gray-200 hover:border-gray-400 transition-all"
          >
            Choose Another Religion
          </button>
          <button
            data-testid="gods-go-home"
            onClick={() => navigate('/')}
            className="bg-gray-800 text-white font-black text-lg py-4 px-8 rounded-full border-b-[6px] border-gray-950 active:border-b-0 active:translate-y-[6px] transition-all hover:bg-gray-700 flex items-center justify-center gap-2 shadow-lg"
          >
            <Home className="w-6 h-6" />
            Back to Studio
          </button>
        </div>
      </div>
    </div>
  );
}
